import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule,
} from "vuex-module-decorators";
import store from "@/store/index";
import LocalDataService from "@/service/LocalDataService";
import ApiAiReport, {
  AiReportBatchResponse,
  AiReportDetailResponse,
  AiReportLanguage,
  AiReportQuarter,
} from "@/api/ApiAiReport";

export interface AiReportDetailState {
  detail: AiReportDetailResponse | null;
  isFetching: boolean;
  activeBatch: AiReportBatchResponse | null;
}

@(Module({ dynamic: true, store, name: "ai-report-detail", namespaced: true }) as unknown as (target: new (...args: unknown[]) => VuexModule) => void)
class AiReportDetailModule extends VuexModule implements AiReportDetailState {
  detail: AiReportDetailResponse | null = null;
  isFetching = false;
  activeBatch: AiReportBatchResponse | null = null;

  @Mutation
  setDetail(value: AiReportDetailResponse | null) {
    this.detail = value;
  }

  @Mutation
  setFetching(value: boolean) {
    this.isFetching = value;
  }

  @Mutation
  setActiveBatch(batch: AiReportBatchResponse | null) {
    this.activeBatch = batch;
  }

  @Action
  async loadDetail({
    businessYear,
    quarter,
    childId,
  }: {
    businessYear: number;
    quarter: AiReportQuarter;
    childId: number;
  }) {
    const nurseryId = LocalDataService.getNurseryId();
    if (!nurseryId) return;
    this.setFetching(true);
    try {
      const response = await ApiAiReport.getDetail({
        nurseryId,
        businessYear,
        quarter,
        childId,
      });
      this.setDetail(response);
    } finally {
      this.setFetching(false);
    }
  }

  @Action
  async regenerate({
    businessYear,
    quarter,
    childId,
    language,
  }: {
    businessYear: number;
    quarter: AiReportQuarter;
    childId: number;
    // 指定時は「翻訳」モード。未指定は単純な再生成。
    language?: AiReportLanguage | null;
  }) {
    const nurseryId = LocalDataService.getNurseryId();
    if (!nurseryId) return null;
    const batch = await ApiAiReport.regenerateChild({
      nurseryId,
      businessYear,
      quarter,
      childId,
      language,
    });
    this.setActiveBatch(batch);
    return batch;
  }

  @Action
  async pollBatch({ batchId }: { batchId: number }) {
    const nurseryId = LocalDataService.getNurseryId();
    if (!nurseryId) return null;
    const batch = await ApiAiReport.getBatch({ nurseryId, batchId });
    this.setActiveBatch(batch);
    return batch;
  }

  @Action
  async setStatus({
    businessYear,
    quarter,
    childId,
    status,
  }: {
    businessYear: number;
    quarter: AiReportQuarter;
    childId: number;
    status: "published" | "draft";
  }) {
    const nurseryId = LocalDataService.getNurseryId();
    if (!nurseryId) return null;
    const response = await ApiAiReport.setStatus({
      nurseryId,
      businessYear,
      quarter,
      childId,
      status,
    });
    if (response) {
      this.setDetail(response);
    }
    return response;
  }
}

export const aiReportDetailModule = getModule(AiReportDetailModule);
